// Real-time Order Status Updates Hook
// Uses Supabase Realtime to listen for order status changes

import { useState, useEffect, useRef } from 'react'
import { RealtimeChannel } from '@supabase/supabase-js'
import { supabase, SecretOrder } from '../lib/supabase'
import { notifyOrderStatus } from '../lib/notifications'
import toast from 'react-hot-toast'
import { EnterpriseToast } from '../components/ui/EnterpriseToast'

interface OrderStatusChange {
  orderId: string
  oldStatus: string
  newStatus: string
  order: SecretOrder
  timestamp: string
}

interface UseOrderRealtimeOptions {
  userId?: string
  orderId?: string
  onStatusChange?: (change: OrderStatusChange) => void
  enableNotifications?: boolean
  enableToasts?: boolean
}

export const useOrderRealtime = (options: UseOrderRealtimeOptions = {}) => {
  const {
    userId,
    orderId,
    onStatusChange,
    enableNotifications = true,
    enableToasts = true
  } = options

  const [isConnected, setIsConnected] = useState(false)
  const [lastUpdate, setLastUpdate] = useState<string | null>(null)
  const channelRef = useRef<RealtimeChannel | null>(null)
  const previousStatusRef = useRef<Record<string, string>>({})

  useEffect(() => {
    if (!userId && !orderId) {
      return
    }

    const channelName = orderId 
      ? `order:${orderId}`
      : `user_orders:${userId}`

    // Create realtime channel
    const channel = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'secret_orders',
          filter: orderId 
            ? `id=eq.${orderId}`
            : `user_id=eq.${userId}`
        },
        async (payload) => {
          console.log('Order update received:', payload)
          
          const updatedOrder = payload.new as SecretOrder
          const oldOrder = payload.old as SecretOrder
          
          // Check if status actually changed
          if (oldOrder.status !== updatedOrder.status) {
            const statusChange: OrderStatusChange = {
              orderId: updatedOrder.id,
              oldStatus: oldOrder.status,
              newStatus: updatedOrder.status,
              order: updatedOrder,
              timestamp: new Date().toISOString()
            }

            // Update previous status tracking
            previousStatusRef.current[updatedOrder.id] = updatedOrder.status

            // Callback for custom handling
            onStatusChange?.(statusChange)

            // Show toast notification
            if (enableToasts) {
              showStatusToast(statusChange)
            }

            // Send push/email notification
            if (enableNotifications && userId) {
              try {
                await notifyOrderStatus(userId, {
                  orderNumber: updatedOrder.order_number,
                  status: updatedOrder.status,
                  statusText: getStatusDisplayText(updatedOrder.status),
                  location: 'Event Location', // Would need to fetch event details
                  pickupTime: updatedOrder.pickup_time,
                  orderUrl: `${window.location.origin}/orders/${updatedOrder.id}`
                })
              } catch (error) {
                console.error('Failed to send status notification:', error)
              }
            }

            setLastUpdate(new Date().toISOString())
          }
        }
      )
      .on('subscribe', (status) => {
        console.log('Realtime subscription status:', status)
        setIsConnected(status === 'SUBSCRIBED')
      })
      .on('error', (error) => {
        console.error('Realtime subscription error:', error)
        setIsConnected(false)
      })

    // Subscribe to channel
    channel.subscribe()
    channelRef.current = channel

    return () => {
      if (channelRef.current) {
        channelRef.current.unsubscribe()
        channelRef.current = null
      }
      setIsConnected(false)
    }
  }, [userId, orderId, onStatusChange, enableNotifications, enableToasts])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (channelRef.current) {
        channelRef.current.unsubscribe()
      }
    }
  }, [])

  return {
    isConnected,
    lastUpdate,
    disconnect: () => {
      if (channelRef.current) {
        channelRef.current.unsubscribe()
        channelRef.current = null
        setIsConnected(false)
      }
    }
  }
}

// Helper function to show appropriate toast for status change
const showStatusToast = (change: OrderStatusChange) => {
  const { newStatus, order } = change

  switch (newStatus) {
    case 'confirmed':
      toast.custom((t) => (
        <EnterpriseToast
          t={t}
          type="success"
          title="Order Confirmed"
          message={`Order ${order.order_number} confirmed!`}
          duration={5000}
        />
      ))
      break

    case 'preparing':
      toast.loading(`👨‍🍳 Order ${order.order_number} is being prepared!`, {
        duration: 4000,
        icon: '🔥'
      })
      break
    
    case 'ready':
      toast.custom((t) => (
        <EnterpriseToast
          t={t}
          type="success"
          title="Ready for Pickup"
          message={`Order ${order.order_number} is ready for pickup!`}
          duration={8000}
        />
      ))
      break

    case 'completed':
      toast.custom((t) => (
        <EnterpriseToast
          t={t}
          type="success"
          title="Order Completed"
          message={`Order ${order.order_number} completed! Thanks!`}
          duration={5000}
        />
      ))
      break

    case 'cancelled':
      toast.custom((t) => (
        <EnterpriseToast
          t={t}
          type="error"
          title="Order Cancelled"
          message={`Order ${order.order_number} was cancelled`}
          duration={5000}
        />
      ))
      break

    case 'transferred':
      toast.custom((t) => (
        <EnterpriseToast
          t={t}
          type="info"
          title="Order Transferred"
          message={`Order ${order.order_number} has been transferred.`}
          duration={5000}
        />
      ))
      break
    
    default:
      toast(`📋 Order ${order.order_number} status updated: ${newStatus}`, {
        duration: 4000
      })
  }
}

// Helper function to get display text for status
const getStatusDisplayText = (status: string): string => {
  switch (status) {
    case 'pending': return 'Payment Pending'
    case 'confirmed': return 'Order Confirmed'
    case 'preparing': return 'Being Prepared'
    case 'ready': return 'Ready for Pickup'
    case 'completed': return 'Completed'
    case 'cancelled': return 'Cancelled'
    case 'transferred': return 'Transferred'
    default: return 'Status Updated'
  }
}

// Hook specifically for tracking multiple orders for a user
export const useUserOrdersRealtime = (userId: string, options: Omit<UseOrderRealtimeOptions, 'userId' | 'orderId'> = {}) => {
  return useOrderRealtime({ ...options, userId })
}

// Hook specifically for tracking a single order
export const useSingleOrderRealtime = (orderId: string, options: Omit<UseOrderRealtimeOptions, 'userId' | 'orderId'> = {}) => {
  return useOrderRealtime({ ...options, orderId })
}